import { ref } from 'vue';
import { z } from 'zod';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';
import { useInternational } from '@/common/locale';
import { useMessage } from '@/common/inject/useMessage';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { getSlideDetailStore, TOTAL_DETAIL } from '@/common/stores/slide-detail';
import { useTargetPermissionCheck } from '@/common/permission/permission.utils';
import { TargetStatusRequestTargetKind } from '@/api-client/target/model';
import type { RolePermissionKeyType } from '@/config/views/permission/rolePermission/rolePermission.type';
import type {
  SlideDetailAdapterParams,
  SlideDetailAdapterResult,
  SlideDetailAdapterResultKey,
} from '@/common/types/slideDetailAdapter.types';
import { createSlideDetailResult } from '@/common/utils/slideDetailAdapterResult';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { getV7ApplicationByWasId } from '@/api-client/application/queries/application-was-controller/application-was-controller';
import type { WasTypeWithNone } from '@/application/utils/types';

const wasDetailInputSchema = z
  .object({
    targetId: z.string().optional(),
    targetName: z.string().optional(),
    rawData: z.record(z.any()).optional(),
    wasId: z.string().optional(),
    wasName: z.string().optional(),
    type: z.string().optional(),
    fromDashboard: z.boolean().optional(),
    timePeriodInfo: z.any().optional(),
    noKeepState: z.boolean().optional(),
    filterTxnName: z.string().optional(),
    fromTime: z.string().optional(),
    toTime: z.string().optional(),
    visibleTabs: z.array(z.string()).optional(),
  })
  .transform(({ targetId, targetName, rawData, wasId, wasName, type, ...rest }) => ({
    ...rest,
    wasId: wasId || rawData?.wasId || targetId || '',
    wasName: wasName || rawData?.wasName || targetName,
    type: type || rawData?.targetType || rawData?.type,
  }))
  .pipe(
    z.object({
      wasId: z.string().min(1),
      wasName: z.string().optional(),
      type: z.string().optional(),
      fromDashboard: z.boolean().optional(),
      timePeriodInfo: z.any().optional(),
      noKeepState: z.boolean().optional(),
      filterTxnName: z.string().optional(),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
      visibleTabs: z.array(z.string()).optional(),
    }),
  );

export type WasDetailInput = z.input<typeof wasDetailInputSchema>;

export const wasDetailRequiredMap = getRequiredMap(wasDetailInputSchema);

const wasDetailFinalSchema = z.object({
  wasId: z.string().min(1),
  wasName: z.string().min(1),
  type: z.string().min(1),
  fromDashboard: z.boolean().optional(),
  timePeriodInfo: z.any().optional(),
  noKeepState: z.boolean().optional(),
  filterTxnName: z.string().optional(),
  fromTime: z.string().optional(),
  toTime: z.string().optional(),
  visibleTabs: z.array(z.string()).optional(),
});

const getMissingParams = (error: z.ZodError, fallbackKeys: readonly string[]) => {
  const missingSet = new Set(
    error.issues
      .map((issue) => issue.path[0])
      .filter((path): path is string => typeof path === 'string'),
  );
  return fallbackKeys.filter((key) => missingSet.has(key)).join(', ');
};

const fetchWasInfo = async ({
  wasId,
  fromTime,
  toTime,
}: {
  wasId: string;
  fromTime?: string;
  toTime?: string;
}) => {
  try {
    const { data } = await getV7ApplicationByWasId(
      wasId,
      {
        fromTime,
        toTime,
      },
      {
        params: { frameName: FRAME_NAMES.WAS_DETAIL.INFORMATION },
      },
    );
    const { wasName, type } = data?.data?.[0] ?? {};
    return { wasId, wasName, type };
  } catch {
    return { wasId: wasId || '', wasName: '', type: 'none' };
  }
};

const resolveWasInfo = async (
  params: z.infer<typeof wasDetailInputSchema>,
): Promise<{
  wasId: string;
  wasName: string;
  type: string;
  fromDashboard?: boolean;
  timePeriodInfo?: unknown;
  noKeepState?: boolean;
  filterTxnName?: string;
  fromTime?: string;
  toTime?: string;
  visibleTabs?: string[];
}> => {
  const hasWasName = Boolean(params.wasName);
  const hasType = Boolean(params.type);
  if (hasWasName && hasType) {
    return {
      wasId: params.wasId,
      wasName: params.wasName ?? '',
      type: params.type ?? '',
      fromDashboard: params.fromDashboard,
      timePeriodInfo: params.timePeriodInfo,
      noKeepState: params.noKeepState,
      filterTxnName: params.filterTxnName,
      fromTime: params.fromTime,
      toTime: params.toTime,
      visibleTabs: params.visibleTabs,
    };
  }

  const wasInfo = await fetchWasInfo({
    wasId: params.wasId,
    fromTime: params.fromTime,
    toTime: params.toTime,
  });

  return {
    wasId: params.wasId,
    wasName: params.wasName || wasInfo.wasName || '',
    type: params.type || wasInfo.type || '',
    fromDashboard: params.fromDashboard,
    timePeriodInfo: params.timePeriodInfo,
    noKeepState: params.noKeepState,
    filterTxnName: params.filterTxnName,
    fromTime: params.fromTime,
    toTime: params.toTime,
    visibleTabs: params.visibleTabs,
  };
};

export const useWasDetailAdapter = (rolePermissionKey?: RolePermissionKeyType) => {
  const isSlideDetailOpened = ref(false);
  const { showErrorMsg } = useMessage();
  const { t } = useInternational();
  const { addTabPanel, addSplitTabPanel, isDetailSlidePermissionDenied } = getSlideDetailStore();
  const { checkIsValidTarget } = useTargetPermissionCheck();

  const validateWasDetail = async (
    params: Partial<WasDetailInput>,
    options: {
      showErrorMessage: boolean;
      checkPermission?: boolean;
      checkParams?: boolean;
      checkTarget?: boolean;
    },
  ): Promise<{
    reason: SlideDetailAdapterResultKey;
    parsedParams?: z.infer<typeof wasDetailFinalSchema>;
    isDeleted?: boolean;
  }> => {
    const { checkPermission = true, checkParams = true, checkTarget = true } = options;

    // 0. 대상 정보 조회 여부 체크
    const inputParsed = wasDetailInputSchema.safeParse(params);
    if (!inputParsed.success) {
      const missingParams = getMissingParams(inputParsed.error, ['wasId']);
      const errorMsg = t('MESSAGE.LINK_MISSING_REQUIRED_PARAMS', {
        value: missingParams || 'wasId',
      });
      if (options.showErrorMessage) showErrorMsg({ msgStr: errorMsg });
      return { reason: 'invalid_params' };
    }
    const normalizedParams = inputParsed.data;

    // 1. 권한 체크
    if (checkPermission) {
      const denied = isDetailSlidePermissionDenied({
        type: TOTAL_DETAIL.WAS,
        rolePermissionKey:
          rolePermissionKey || ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_WAS,
        showError: options.showErrorMessage,
      });
      if (denied) return { reason: 'permission_denied' };
    }

    // 2. 타겟 유효성 검증
    let isDeleted = false;
    if (checkTarget) {
      const targetValidation = await checkIsValidTarget(
        {
          targetId: normalizedParams.wasId,
          targetKind: TargetStatusRequestTargetKind.APPLICATION,
        },
        {
          showMessageIfTargetNil: options.showErrorMessage,
          showErrorMessage: false,
          isValidateDeletedTarget: false,
        },
      );
      if (targetValidation.reason === 'deleted') {
        isDeleted = true;
      } else if (!targetValidation.valid) {
        if (options.showErrorMessage) showErrorMsg({ msgStr: t('NOTI.PRODUCT.TEMP_SERVER_ERROR') });
        return { reason: targetValidation.reason };
      }
    }

    // 3. 대상 정보 조회 및 필수값 검증
    if (checkParams) {
      const mergedParams = await resolveWasInfo(normalizedParams);
      const finalParsed = wasDetailFinalSchema.safeParse(mergedParams);
      if (!finalParsed.success) {
        const missingParams = getMissingParams(finalParsed.error, ['wasId', 'wasName', 'type']);
        const errorMsg = t('MESSAGE.LINK_MISSING_REQUIRED_PARAMS', {
          value: missingParams || 'wasId, wasName, type',
        });
        if (options.showErrorMessage) showErrorMsg({ msgStr: errorMsg });
        return { reason: 'invalid_params' };
      }
      return { reason: 'ok', parsedParams: finalParsed.data, isDeleted };
    }

    return { reason: 'ok', isDeleted };
  };

  const openWasDetail = async ({
    params,
    options,
    selectedTab,
    state,
    currentState,
    init,
  }: SlideDetailAdapterParams<Partial<WasDetailInput>>): Promise<SlideDetailAdapterResult> => {
    const {
      runCheckPermission = true,
      showErrorMessage = true,
      runTargetValidation = true,
      openMode = 'main',
      keepSplitTab = false,
    } = options ?? {};

    const { reason, parsedParams, isDeleted } = await validateWasDetail(params, {
      showErrorMessage,
      checkPermission: runCheckPermission,
      checkTarget: runTargetValidation,
    });
    if (reason !== 'ok' || !parsedParams) return createSlideDetailResult(reason);
    const validResult = parsedParams;

    // 4. 탭 패널 열기
    const tabPanelInfo = {
      type: TOTAL_DETAIL.WAS,
      name: validResult.wasName,
      was: {
        wasId: validResult.wasId,
        type: validResult.type as WasTypeWithNone,
        fromDashboard: validResult.fromDashboard,
        timePeriodInfo: validResult.timePeriodInfo,
        filterTxnName: validResult.filterTxnName,
        deleted: isDeleted,
        fromTime: validResult.fromTime,
        toTime: validResult.toTime,
        visibleTabs: validResult.visibleTabs,
      },
      init,
      state: { ...state, ...(selectedTab !== undefined ? { selectedTab } : {}) },
      keepSplitTab: openMode === 'split' ? keepSplitTab : undefined,
    } as const;

    if (openMode === 'split') {
      addSplitTabPanel(tabPanelInfo);
      isSlideDetailOpened.value = true;
    } else {
      const result = addTabPanel(tabPanelInfo, {
        ...currentState,
        noKeepState: currentState?.noKeepState || validResult.noKeepState === true,
      });
      isSlideDetailOpened.value = getSlideDetailStore().isOpened;
      return createSlideDetailResult(result);
    }

    return createSlideDetailResult('ok');
  };

  return { isSlideDetailOpened, openWasDetail };
};
